import React from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Building2, Heart, Scale, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { mockData } from '../data/mockData';

export const Home = () => {
  const { hero, pillars, accreditationStages } = mockData;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1562774053-701939374585?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="University campus promoting health"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-burgundy-50/65 to-white/75"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            {/* Información a la izquierda */}
            <div className="flex-[1.2] text-center md:text-left flex flex-col justify-center min-w-0">
              <h1 className="text-4xl md:text-6xl font-bold text-burgundy-900 mb-6">
                {hero.title}
              </h1>
              <p className="text-xl md:text-2xl text-burgundy-700 mb-8 max-w-4xl md:mx-0 mx-auto">
                {hero.subtitle}
              </p>
              <p className="text-lg text-gray-600 mb-10 max-w-3xl md:mx-0 mx-auto">
                {hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 md:justify-start justify-center">
                {hero.ctaButtons.map((button, index) => (
                  <Button
                    key={index}
                    asChild
                    size="lg"
                    className={button.primary
                      ? "bg-burgundy-700 hover:bg-burgundy-800 text-white px-8 py-4 text-lg"
                      : "bg-white border-2 border-burgundy-700 text-burgundy-700 hover:bg-burgundy-50 px-8 py-4 text-lg"
                    }
                  >
                    <Link to={button.href}>
                      {button.text}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                ))}
              </div>
            </div>
            {/* Logotipo a la derecha */}
            <div className="flex-[0.8] flex justify-center md:justify-end items-center min-w-[220px]">
              <img
                src="/assets/images/logo_ups.png"
                alt="UPS Logo"
                className="h-56 w-56 md:h-72 md:w-72 object-contain drop-shadow-lg"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Brief Presentation */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              ¿Qué son las Universidades Promotoras de la Salud?
            </h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Las Universidades Promotoras de la Salud (UPS) son instituciones de educación superior
              comprometidas con la creación de entornos que fomenten la salud y el bienestar de toda
              su comunidad universitaria, integrando la promoción de la salud en todas sus funciones
              sustantivas: docencia, investigación y extensión.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-burgundy-800 mb-4">
                ¿Por qué acreditarse como UPS?
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Reconocimiento internacional de calidad</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Mejoramiento continuo de procesos institucionales</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Fortalecimiento del bienestar universitario</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">Red global de universidades saludables</span>
                </li>
              </ul>
            </div>
            <div className="bg-burgundy-50 p-8 rounded-lg">
              <h4 className="text-xl font-bold text-burgundy-800 mb-4">Modelo Decimal</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-burgundy-700">10</div>
                  <div className="text-sm text-gray-600">Rubros</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-burgundy-700">100</div>
                  <div className="text-sm text-gray-600">Ítems</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-burgundy-700">1000</div>
                  <div className="text-sm text-gray-600">Indicadores</div>
                </div>
                <div className='text-center col-span-3 md:col-span-1 md:col-start-2'>
                  <div className="text-3xl font-bold text-burgundy-700">10000</div>
                  <div className="text-sm text-gray-600">Fuentes bibliográficas</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Cuatro Ejes del Modelo UPS
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Nuestro modelo se fundamenta en cuatro pilares esenciales que garantizan
              un enfoque integral de la promoción de la salud universitaria.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar, index) => {
              const IconComponent = pillar.icon === 'GraduationCap' ? GraduationCap :
                pillar.icon === 'Building2' ? Building2 : 
                pillar.icon === 'Heart' ? Heart : Scale;

              return (
                <Card key={pillar.id} className="h-full hover:shadow-lg transition-shadow duration-300 flex flex-col justify-between">
                  <CardHeader className="text-center">
                    <div className="mx-auto w-16 h-16 bg-burgundy-100 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="h-8 w-8 text-burgundy-700" />
                    </div>
                    <CardTitle className="text-xl text-burgundy-800 mb-4">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col justify-between flex-grow">
                    <p className="text-gray-600 mb-4">{pillar.description}</p>
                    <ul className="space-y-2 mb-4">
                      {pillar.details.slice(0, 3).map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-gray-600">{detail}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full border-burgundy-300 text-burgundy-700 hover:bg-burgundy-50 mt-auto"
                    >
                      <Link to="/pilares">Conocer más</Link>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Summary Card */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-r from-burgundy-50 to-white border-burgundy-200 shadow-xl">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Descripción a la izquierda */}
                <div className="order-2 lg:order-1">
                  <h3 className="text-2xl md:text-3xl font-bold text-burgundy-900 mb-6">
                    Desarrollo Humano Sostenible
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    El desarrollo humano sostenible en una Universidad Promotora de la Salud se sustenta en cuatro pilares: 
                    educación para la salud y habilidades para la vida, entornos saludables, servicios de salud, nutrición y vida activa, 
                    y políticas públicas saludables. Este modelo integral fomenta bienestar, equidad y resiliencia en la 
                    comunidad universitaria y social.
                  </p>
                </div>
                
                {/* Imagen a la derecha */}
                <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
                  <img
                    src="/assets/images/casita.png"
                    alt="Universidad Promotora de la Salud"
                    className="w-full max-w-sm h-auto object-contain drop-shadow-lg"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* SIEVAS Accreditation Block */}
      <section className="py-16 bg-burgundy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Acreditación con SIEVAS
            </h2>
            <p className="text-lg text-burgundy-100 max-w-3xl mx-auto">
              Sistema Integral de Evaluación y Acreditación de Universidades Saludables
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
            {accreditationStages.slice(0, 4).map((stage, index) => (
              <div key={stage.id} className="text-center">
                <div className="bg-burgundy-700 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold">{stage.id}</span>
                </div>
                <h4 className="font-semibold mb-2">{stage.title}</h4>
                <p className="text-sm text-burgundy-200">{stage.description}</p>
                {index < 4 && (
                  <ArrowRight className="h-6 w-6 mx-auto mt-4 text-burgundy-300 hidden md:block" />
                )}
              </div>
            ))}
            <div className="text-center">
              <div className="bg-gold-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
              <h4 className="font-semibold mb-2">Acreditación</h4>
              <p className="text-sm text-burgundy-200">Reconocimiento oficial</p>
              {
                <CheckCircle className="h-6 w-6 mx-auto mt-4 text-green-500 hidden md:block" />
              }
            </div>
          </div>

          <div className="text-center mt-12 mb-12">
            <Button
              size="lg"
              className="bg-white text-burgundy-900 hover:bg-gray-100 px-8 py-4 text-lg"
            >
              <Link to="/contacto">Solicitar Acceso a SIEVAS</Link>
            </Button>
          </div>
          <hr className="border-burgundy-700" />
        </div>
      </section>
    </div>
  );
};